import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { fetchProducts, addToCart } from '../../../redux/WithcookieSlice';
import { updateComponent } from '../../../redux/editorSlice';
import { useTranslation } from '../../../redux/TranslationHelper';
import { toast } from 'react-toastify';
import { ProductItem as ProductItemType, ProductSliderLogicReturn } from './ProductSlider.types';
import { ComponentData, ComponentRenderMode } from '../../../types/component-skin';

/**
 * ProductSlider 컴포넌트 로직 훅
 * 원본 ProductSlider.component.tsx의 로직을 분리하여 스킨에서 사용할 수 있도록 구성
 */
const useProductSliderLogic = (componentData: ComponentData, mode: ComponentRenderMode): ProductSliderLogicReturn => {
  // 호환성을 위한 props 변환
  const props = { ...componentData.props, ...componentData.componentProps, ...componentData };

  // Redux hooks
  const dispatch = useDispatch();

  // 다국어 지원
  const { t } = useTranslation();

  // Props 추출
  const { id, componentProps = {}, style = {} } = props;

  // 컴포넌트 프로퍼티 설정값
  const {
    sliderTitle = t('인기 상품'),
    showTitle = true,
    titleFontSize = '18px',
    titleFontWeight = '600',
    titleColor = '#333',
    itemsPerSlide = 4, // eslint-disable-line @typescript-eslint/no-unused-vars
    autoSlide = true,
    autoSlideInterval = 3000,
    productIds = '',
    include_product_ids = '',
    exclude_product_ids = '',
    include_category_ids = '',
    exclude_category_ids = '',
    showStock = false,
    showAddToCart = true,
    showPrice = true,
    cartButtonColor = '#007bff'
  } = componentProps;

  // 상태 변수들
  const [products, setProducts] = useState<ProductItemType[]>([]);
  const [translateX, setTranslateX] = useState(0);
  const [slideWidth, setSlideWidth] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleEditValue, setTitleEditValue] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [innerContainerWidth, setInnerContainerWidth] = useState(0);

  // 스와이프 기능을 위한 상태
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const swipeThreshold = 50;

  // 참조 변수들
  const containerRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const slideRef = useRef<HTMLDivElement>(null);
  const fetchedRef = useRef(false);
  const currentFilterRef = useRef<string>('');

  // 숫자값 변환
  const autoSlideIntervalNum = parseInt(String(autoSlideInterval)) || 3000;

  // 타이틀 스타일 객체
  const titleStyle = {
    fontSize: titleFontSize,
    fontWeight: titleFontWeight,
    color: titleColor
  };

  // 컨테이너 스타일
  const containerStyle = {
    backgroundColor: style.backgroundColor || 'transparent',
    borderWidth: style.borderWidth || '1px',
    borderColor: style.borderColor || '#eeeeee',
    borderRadius: style.borderRadius || '0px',
    boxShadow: style.boxShadow || 'none',
    padding: style.padding || '0 15px',
    paddingTop: style.paddingTop || '0',
    paddingRight: style.paddingRight || '15px',
    paddingBottom: style.paddingBottom || '0',
    paddingLeft: style.paddingLeft || '15px',
    margin: style.margin || '0',
    marginTop: style.marginTop || '0',
    marginRight: style.marginRight || '0',
    marginBottom: style.marginBottom || '0',
    marginLeft: style.marginLeft || '0',
    ...style
  };

  // 상품 ID 배열로 변환
  const includeProductIds = useMemo(() => {
    if (!include_product_ids && !productIds) return [];
    const sourceStr = include_product_ids || productIds;
    if (typeof sourceStr !== 'string' || sourceStr.trim() === '') return [];
    return sourceStr.split(',').map(id => id.trim()).filter(id => id !== '');
  }, [include_product_ids, productIds]);

  const excludeProductIds = useMemo(() => {
    if (!exclude_product_ids || typeof exclude_product_ids !== 'string' || exclude_product_ids.trim() === '') return [];
    return exclude_product_ids.split(',').map(id => id.trim()).filter(id => id !== '');
  }, [exclude_product_ids]);

  const includeCategoryIds = useMemo(() => {
    if (!include_category_ids || typeof include_category_ids !== 'string' || include_category_ids.trim() === '') return [];
    return include_category_ids.split(',').map(id => id.trim()).filter(id => id !== '');
  }, [include_category_ids]);

  const excludeCategoryIds = useMemo(() => {
    if (!exclude_category_ids || typeof exclude_category_ids !== 'string' || exclude_category_ids.trim() === '') return [];
    return exclude_category_ids.split(',').map(id => id.trim()).filter(id => id !== '');
  }, [exclude_category_ids]);

  // 무한 슬라이드를 위한 전체 상품 배열 (3배 복제)
  const allProducts = useMemo(() => {
    if (!products || products.length === 0) return [];
    return products; // 이미 useEffect에서 3배 복제되어 있음
  }, [products]);

  // 슬라이더 타이틀 더블 클릭 핸들러
  const handleTitleDoubleClick = useCallback(() => {
    setIsEditingTitle(true);
    setTitleEditValue(sliderTitle);
  }, [sliderTitle]);

  // 편집 모드에서 블러 이벤트 핸들러
  const handleTitleBlur = useCallback(() => {
    setIsEditingTitle(false);
    dispatch(updateComponent({
      id,
      changes: {
        componentProps: {
          ...componentProps,
          sliderTitle: titleEditValue
        }
      }
    }));
  }, [id, componentProps, dispatch, titleEditValue]);

  // 터치 이벤트 핸들러들
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setTouchEnd(null);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);

  // 슬라이드 이동 함수
  const moveSlide = useCallback((direction: 'next' | 'prev') => {
    if (!slideWidth || !innerContainerWidth) return;

    let moveDistance: number;
    if (isMobile) {
      moveDistance = innerContainerWidth;
    } else if (isTablet) {
      moveDistance = innerContainerWidth / 2;
    } else {
      moveDistance = innerContainerWidth / 4;
    }

    if (direction === 'next') {
      setTranslateX(prev => prev - moveDistance);
    } else if (direction === 'prev') {
      setTranslateX(prev => prev + moveDistance);
    }
  }, [slideWidth, innerContainerWidth, isMobile, isTablet]);

  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > swipeThreshold;
    const isRightSwipe = distance < -swipeThreshold;

    if (isLeftSwipe) {
      moveSlide('next');
    }

    if (isRightSwipe) {
      moveSlide('prev');
    }

    setTouchStart(null);
    setTouchEnd(null);
  }, [touchStart, touchEnd, moveSlide, swipeThreshold]);

  // 필터링 옵션 변경 핸들러
  const handleFilterOptionChange = useCallback((optionName: string, value: string) => {
    dispatch(updateComponent({
      id,
      changes: {
        componentProps: {
          ...componentProps,
          [optionName]: value
        }
      }
    }));

    fetchedRef.current = false;
    setLoading(true);
  }, [id, componentProps, dispatch]);

  // API 필터링 옵션 생성 함수
  const getFilterOptions = useCallback(() => {
    const options: any = {
      page: 1,
      per_page: 100
    };

    const toIntArray = (value: any): number[] => {
      if (!value) return [];

      if (Array.isArray(value)) {
        return value
          .map(id => typeof id === 'string' ? parseInt(id, 10) : Number(id))
          .filter(id => !isNaN(id));
      }

      if (typeof value === 'string' && value.trim() !== '') {
        return value
          .split(',')
          .map(id => id.trim())
          .filter(id => id !== '')
          .map(id => parseInt(id, 10))
          .filter(id => !isNaN(id));
      }

      const parsed = typeof value === 'string' ? parseInt(value, 10) : Number(value);
      return !isNaN(parsed) ? [parsed] : [];
    };

    const includeProductIdsInt = toIntArray(includeProductIds);
    const excludeProductIdsInt = toIntArray(excludeProductIds);
    const includeCategoryIdsInt = toIntArray(includeCategoryIds);
    const excludeCategoryIdsInt = toIntArray(excludeCategoryIds);

    if (includeProductIdsInt.length > 0) {
      options.include_product_ids = includeProductIdsInt;
    }

    if (excludeProductIdsInt.length > 0) {
      options.exclude_product_ids = excludeProductIdsInt;
    }

    if (includeCategoryIdsInt.length > 0) {
      options.include_category_ids = includeCategoryIdsInt;
    }

    if (excludeCategoryIdsInt.length > 0) {
      options.exclude_category_ids = excludeCategoryIdsInt;
    }

    return options;
  }, [includeProductIds, excludeProductIds, includeCategoryIds, excludeCategoryIds]);

  // 가격 포맷팅 함수
  const formatPrice = useCallback((price: number) => {
    if (!price) return t('₩0');
    return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(price);
  }, [t]);

  // 장바구니에 상품 추가
  const handleAddToCart = useCallback(async (product: any, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await dispatch(addToCart({
        product_id: product.id,
        count: 1,
        variant_id: product.variant_id || null
      }) as any);

      toast.success(t('장바구니에 추가되었습니다.'));
    } catch (error) {
      console.error(t('장바구니 추가 실패:'), error);
      toast.error(t('장바구니 추가에 실패했습니다.'));
    }
  }, [dispatch, t]);

  // 상품 클릭 핸들러
  const handleProductClick = useCallback((product: any) => {
    // 상품 상세 페이지로 이동
    if (product && product.id) {
      // componentData.utils가 있으면 사용 (React Router 등)
      if (componentData.utils?.navigate) {
        componentData.utils.navigate(`/products/${product.id}`);
      } else {
        // 없으면 기본 페이지 이동
        window.location.href = `/products/${product.id}`;
      }
    }
  }, [componentData.utils]);

  // 필터링 설정 렌더링 함수 (JSX는 스킨에서 처리)
  const renderFilteringSettings = useCallback(() => {
    const isEditMode = window.location.pathname.includes('/editor');
    return isEditMode;
  }, []);

  // 기본 상품 데이터 (외부 스킨에서 접근 가능하도록 분리)
  const defaultProducts: ProductItemType[] = [
    { id: 1, title: "스마트폰", price: 1200000, salePrice: 1000000, image: '' },
    { id: 2, title: "노트북", price: 1800000, image: '' },
    { id: 3, title: "이어버드", price: 250000, salePrice: 199000, image: '' },
    { id: 4, title: "워치", price: 350000, salePrice: 299000, image: '' },
    { id: 5, title: "태블릿", price: 950000, salePrice: 850000, image: '' },
    { id: 6, title: "헤드셋", price: 180000, salePrice: 150000, image: '' }
  ];

  // 상품 정보 가져오기
  useEffect(() => {

    const options = getFilterOptions();
    const filterOptionsString = JSON.stringify(options);
    const isFilterChanged = currentFilterRef.current !== filterOptionsString;

    if (fetchedRef.current && !isFilterChanged) return;

    currentFilterRef.current = filterOptionsString;
    setLoading(true);

    const fetchFilteredProducts = async () => {
      try {
        const resultAction = await dispatch(fetchProducts(options) as any);

        if (fetchProducts.fulfilled.match(resultAction)) {
          const responseData = resultAction.payload;
          const filteredProducts = responseData.products || [];

          if (filteredProducts && filteredProducts.length > 0) {
            fetchedRef.current = true;

            let repeatedProducts: ProductItemType[] = [];
            const minItems = 12;
            let baseProducts = [...filteredProducts];

            while (baseProducts.length < minItems) {
              baseProducts = [...baseProducts, ...filteredProducts];
            }

            repeatedProducts = [...baseProducts, ...baseProducts, ...baseProducts];
            setProducts(repeatedProducts);
          } else {
            const repeatedDummy = [...defaultProducts, ...defaultProducts, ...defaultProducts];
            setProducts(repeatedDummy);
          }
        } else {
          throw new Error(t('상품 목록을 가져오는데 실패했습니다.'));
        }
      } catch (error) {
        console.error(t('[상품 슬라이더] 상품 정보 가져오기 실패:'), error);
        const repeatedDummy = [...defaultProducts, ...defaultProducts, ...defaultProducts];
        setProducts(repeatedDummy);
      } finally {
        setLoading(false);
      }
    };

    fetchFilteredProducts();

    return () => {
      fetchedRef.current = false;
    };
  }, [dispatch, getFilterOptions, t, includeProductIds, excludeProductIds, includeCategoryIds, excludeCategoryIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // 모바일/태블릿 화면 감지
  useEffect(() => {
    const checkDeviceType = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };

    checkDeviceType();
    window.addEventListener('resize', checkDeviceType);

    return () => {
      window.removeEventListener('resize', checkDeviceType);
    };
  }, []);

  // 슬라이드 너비 계산
  useEffect(() => {
    if (containerRef.current) {
      const calculateWidths = () => {
        try {
          const containerStyles = window.getComputedStyle(containerRef.current!);
          const paddingLeft = parseFloat(containerStyles.paddingLeft);
          const paddingRight = parseFloat(containerStyles.paddingRight);

          const containerWidth = containerRef.current!.offsetWidth;
          const innerWidth = containerWidth - paddingLeft - paddingRight;

          setInnerContainerWidth(innerWidth);

          let itemsPerRow = 4;
          if (isMobile) {
            itemsPerRow = 1;
          } else if (isTablet) {
            itemsPerRow = 2;
          }

          const exactSlideWidth = innerWidth / itemsPerRow;
          setSlideWidth(exactSlideWidth);

          setIsTransitioning(false);
          setTranslateX(0);

          requestAnimationFrame(() => {
            setIsTransitioning(true);
          });
        } catch (error) {
          console.error(t('슬라이드 너비 계산 오류:'), error);
        }
      };

      calculateWidths();
      window.addEventListener('resize', calculateWidths);

      return () => {
        window.removeEventListener('resize', calculateWidths);
      };
    }
  }, [isMobile, isTablet, t]);

  // 무한 슬라이딩 효과
  useEffect(() => {
    if (!slideWidth || !innerContainerWidth || products.length === 0 || !sliderRef.current) return;

    const originalProductCount = products.length / 3;
    const forwardBoundary = -slideWidth * originalProductCount;
    const backwardBoundary = 0;

    const checkBoundaries = () => {
      if (translateX <= forwardBoundary) {
        setIsTransitioning(false);
        setTranslateX(translateX + originalProductCount * slideWidth);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsTransitioning(true);
          });
        });
      } else if (translateX > backwardBoundary) {
        setIsTransitioning(false);
        setTranslateX(translateX - originalProductCount * slideWidth);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsTransitioning(true);
          });
        });
      }
    };

    const handleTransitionEnd = () => {
      checkBoundaries();
    };

    const currentSlider = sliderRef.current;
    currentSlider.addEventListener('transitionend', handleTransitionEnd);

    return () => {
      if (currentSlider) {
        currentSlider.removeEventListener('transitionend', handleTransitionEnd);
      }
    };
  }, [translateX, slideWidth, innerContainerWidth, products.length, t]);

  // 자동 슬라이드
  useEffect(() => {
    if (!autoSlide || !slideWidth || !innerContainerWidth || products.length === 0) return;

    const interval = setInterval(() => {
      moveSlide('next');
    }, autoSlideIntervalNum);

    return () => clearInterval(interval);
  }, [autoSlide, autoSlideIntervalNum, innerContainerWidth, moveSlide, products.length, slideWidth]);

  // 데이터와 액션을 ComponentSkinWrapper 형식으로 반환
  return {
    data: {
      // 상품 데이터
      allProducts,
      defaultProducts, // 외부 스킨에서 사용할 수 있는 기본 상품 데이터
      products: allProducts, // 외부 스킨 호환성을 위한 별칭
      loading,

      // 무한 슬라이드 관련 상태
      translateX,
      slideWidth,
      isTransitioning,
      isMobile,
      isTablet,
      innerContainerWidth,

      // 터치/스와이프 상태
      touchStart,
      touchEnd,

      // 컴포넌트 설정값
      sliderTitle,
      showTitle,
      titleFontSize,
      titleFontWeight,
      titleColor,
      showPrice,
      showAddToCart,
      showStock,
      cartButtonColor,

      // 편집 모드 관련
      isEditingTitle,
      titleEditValue,

      // 필터링 설정값
      include_product_ids,
      exclude_product_ids,
      include_category_ids,
      exclude_category_ids,

      // 스타일
      containerStyle,
      titleStyle,

      // 참조
      containerRef,
      sliderRef,
      slideRef
    },

    actions: {
      // 무한 슬라이드 이동
      moveSlide,

      // 터치/스와이프 이벤트
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,

      // 장바구니 및 상품 클릭
      handleAddToCart,
      handleProductClick,

      // 제목 편집 관련
      handleTitleDoubleClick,
      handleTitleBlur,
      setTitleEditValue,

      // 필터링 설정 렌더링
      renderFilteringSettings,

      // 필터링 설정 변경
      handleFilterOptionChange,

      // 가격 포맷팅
      formatPrice
    }
  };
};

export default useProductSliderLogic;
